import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Calculator, Hammer, Home, Shield, ArrowRight, Sparkles } from 'lucide-react';

const SIMULATORS = [
  {
    id: 'ia-activites',
    label: 'Simulateur IA activités',
    desc: 'Sélection IA des activités → contribution + CP/CG',
    icon: Sparkles,
    color: 'from-violet-600 to-fuchsia-700',
    iconBg: 'bg-violet-100 text-violet-700',
    route: (membre) => `/calculateur-batico?membre_id=${membre?.id || ''}&ia_membre=${membre?.id || ''}&from=ia_activites`,
  },
  {
    id: 'rcds-batico',
    label: 'BATICO — RCDS Solidaire',
    desc: 'Garantie décennale BTP & gros œuvre',
    icon: Hammer,
    color: 'from-blue-600 to-cyan-700',
    iconBg: 'bg-blue-100 text-blue-700',
    route: (membre) => {
      const params = new URLSearchParams();
      params.set('nouveau', 'true');
      params.set('from', 'souscription');
      if (membre?.id) params.set('membre_id', membre.id);
      if (membre?.raison_sociale || membre?.entreprise) {
        params.set('raison_sociale', membre.raison_sociale || membre.entreprise);
      }
      if (membre?.siret) params.set('siret', membre.siret);
      if (membre?.nom) params.set('nom', membre.nom);
      if (membre?.prenom) params.set('prenom', membre.prenom);
      if (membre?.email) params.set('email', membre.email);
      if (membre?.telephone) params.set('telephone', membre.telephone);
      if (membre?.adresse) params.set('adresse', membre.adresse);
      if (membre?.code_postal) params.set('code_postal', membre.code_postal);
      if (membre?.ville) params.set('ville', membre.ville);
      if (membre?.statut_juridique || membre?.forme_juridique) {
        params.set('statut_juridique', membre.statut_juridique || membre.forme_juridique);
      }
      if (membre?.territoire) params.set('territoire', membre.territoire);
      return `/calculateur-batico?${params.toString()}`;
    },
  },
  {
    id: 'dos-domium',
    label: 'DOS DOMIUM',
    desc: 'Dommages-Ouvrage Solidaire',
    icon: Home,
    color: 'from-emerald-600 to-teal-700',
    iconBg: 'bg-emerald-100 text-emerald-700',
    route: () => `/simulateur-dos`,
  },
  {
    id: 'ftpe',
    label: 'FTPE Outremer',
    desc: 'Fonds de soutien aux TPE',
    icon: Shield,
    color: 'from-purple-600 to-indigo-700',
    iconBg: 'bg-purple-100 text-purple-700',
    route: () => `/adhesion-ftpe-workflow`,
  },
];

export function EstimationSimulatorPickerModal({ open, onClose, membre }) {
  const navigate = useNavigate();
  if (!membre) return null;

  const handlePick = (sim) => {
    onClose(false);
    navigate(sim.route(membre));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="estimation-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-purple-600" />
            Estimation rapide
          </DialogTitle>
          <DialogDescription>
            Choisissez le simulateur à lancer pour{' '}
            <strong>{membre.prenom} {membre.nom}</strong>
            {membre.raison_sociale && <> — <span className="text-slate-500">{membre.raison_sociale}</span></>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          {SIMULATORS.map((sim) => {
            const Icon = sim.icon;
            return (
              <button
                key={sim.id}
                type="button"
                onClick={() => handlePick(sim)}
                data-testid={`simulator-picker-${sim.id}`}
                className="group relative text-left rounded-xl border-2 border-slate-200 bg-white hover:border-purple-400 hover:shadow-lg transition-all duration-200 p-4 overflow-hidden"
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${sim.color}`} />
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${sim.iconBg}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{sim.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{sim.desc}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-purple-600 transition-colors shrink-0 mt-1" />
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EstimationSimulatorPickerModal;
